import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submitBtn", "errors"]
  static values = { labelsUrl: String }

  async save() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this._hideErrors()
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "Saving..."

    try {
      // Collect all role fields from the modal
      const hiddenInputs = this.element.querySelectorAll("[data-re-role-field-target='hidden'][data-role-label]")
      const promises = []

      hiddenInputs.forEach(input => {
        const label = input.dataset.roleLabel
        const email = input.value.trim()

        // Resolve user_id: from data attribute or from members list
        let userId = 0
        if (email) {
          // Try data-user-id first
          if (input.dataset.userId) {
            userId = parseInt(input.dataset.userId) || 0
          }
          // Fallback: find from members JSON on parent controller
          if (!userId) {
            const roleFieldEl = input.closest("[data-controller*='re-role-field']")
            if (roleFieldEl) {
              const membersAttr = roleFieldEl.dataset.reRoleFieldMembersValue
              if (membersAttr) {
                try {
                  const members = JSON.parse(membersAttr)
                  const member = members.find(m => m.email === email)
                  if (member && member.id) userId = member.id
                } catch { /* ignore */ }
              }
            }
          }
        }

        promises.push(
          fetch(this.labelsUrlValue, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "X-CSRF-Token": csrfToken
            },
            body: JSON.stringify({ label, user_id: userId, email })
          }).then(r => r.json())
        )
      })

      const results = await Promise.all(promises)

      const failed = results.filter(r => !r.success)

      // Update card DOM with returned names
      const card = document.getElementById("re-responsibilities-card-body")
      if (card) {
        results.forEach(result => {
          if (result.success && result.label) {
            const slug = result.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")
            const el = card.querySelector(`[data-field="role-${slug}"]`)
            if (el) el.textContent = result.user_name || "—"
          }
        })
      }

      if (failed.length > 0) {
        const messages = failed.flatMap(r =>
          r.errors && r.errors.length ? r.errors : [`Failed to save: ${r.label}. Please try again.`]
        )
        this._showErrors(messages)
        return
      }

      this._closeModal()
      this._showFlash("Responsibilities updated successfully.")
    } catch {
      this._showErrors(["A network error occurred. Please try again."])
    } finally {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = "Save Changes"
    }
  }

  _showErrors(messages) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.innerHTML = messages.map(m => `<p>${m}</p>`).join("")
    this.errorsTarget.classList.remove("hidden")
  }

  _hideErrors() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.classList.add("hidden")
    this.errorsTarget.innerHTML = ""
  }

  _closeModal() {
    this.element.classList.add("hidden")
    document.body.style.overflow = ""
  }

  _showFlash(message) {
    const container = document.createElement("div")
    container.className = "flash-toast-notice"
    container.dataset.controller = "flash"
    container.dataset.flashDismissAfterValue = "5000"
    container.setAttribute("role", "alert")

    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>`

    const msgEl = document.createElement("p")
    msgEl.className = "flex-1 text-sm"
    msgEl.textContent = message

    container.appendChild(iconDiv)
    container.appendChild(msgEl)
    document.body.insertBefore(container, document.body.firstChild)
  }
}
